import cloneDeep from 'lodash/cloneDeep';
import type {
  CollectionEventConfig,
  CollectionEventRewardProgress,
  RewardConfig,
  RewardConfigToCalculate,
} from '../types';
import type { CollectionEventDependencies } from '../CollectionEventController';
import MathUtils from '../../../utils/MathUtils';
import type { TileColor } from '../../../game/boardMVC/TileColor';
import { getTimeLimitHandler } from '../../../libs/timeLimit/getTimeLimitHandler';

export function getUTCWeekNumber(date = new Date()): number {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);

  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((+d - +yearStart) / 86400000 + 1) / 7);
}

export function startEvent(dependencies: CollectionEventDependencies, config: CollectionEventConfig): void {
  saveToUserData(dependencies, {
    ...cloneDeep(config),
    id: `${config.id}!@!${getUTCWeekNumber()}_${new Date().getUTCFullYear()}`,
    timeLimit: {
      ...config.timeLimit,
      startedAt: getTimeLimitHandler(config.timeLimit).getStartDate(),
    },
  });
}

export function clearCollectionEvent(dependencies: CollectionEventDependencies): void {
  const eventInstance = dependencies.observable.getValue().collectionEvent.currentEvent;
  if (!eventInstance) return;

  dependencies.onEvent?.onEventFinished?.(
    eventInstance,
    getCollectionEventRewardProgress(eventInstance, eventInstance.tokensCollected).rewardIndex,
  );

  dependencies.observable.updateValue({
    collectionEvent: {
      ...dependencies.observable.getValue().collectionEvent,
      currentEvent: null,
    },
  });
}

export function findEventToStart(dependencies: CollectionEventDependencies): CollectionEventConfig | null {
  const { observable } = dependencies;
  const { regularEvents } = dependencies.config;

  if (observable.getValue().levelIndex < 10) {
    return null;
  }

  return regularEvents.find((eventConfig) => getTimeLimitHandler(eventConfig.timeLimit).canStart()) ?? null;

  // const prioEvent = priorityEvents.find((eventConfig) => timeLimitCanStart(eventConfig.timeLimit));
  // if (prioEvent) return prioEvent;
  //
  // let regularEventIndex = 0;
  // const lastEventId = getCurrentEventInstance(dependencies)?.id;
  // if (lastEventId) {
  //   regularEventIndex = regularEvents.findIndex((eventConfig) => eventConfig.id === lastEventId) + 1;
  // }
  //
  //
  //
  // console.log('COLLECTION eventToSTart', regularEvents[regularEventIndex % regularEvents.length] ?? null);
  //
  // return regularEvents[regularEventIndex % regularEvents.length] ?? null;
}

export function getCurrentEventInstance(dependencies: CollectionEventDependencies): CollectionEventConfig | null {
  const { observable } = dependencies;
  const { collectionEvent } = observable.getValue();
  return collectionEvent?.currentEvent ?? null;
}

export function getTokensToCollect(dependencies: CollectionEventDependencies): TileColor[] | null {
  return getCurrentEventInstance(dependencies)?.tokenType ?? null;
}

export function getTempTokensAmount(dependencies: CollectionEventDependencies): number | null {
  const eventInstance = getCurrentEventInstance(dependencies);
  return eventInstance?.tempTokens ?? null;
}

export function transferTempTokensInCollectionEvent(dependencies: CollectionEventDependencies): void {
  const { collectionEvent } = dependencies.observable.getValue();
  const eventInstance = collectionEvent?.currentEvent;

  if (!eventInstance) return;

  const { tempTokens } = eventInstance;

  if (tempTokens === 0) return;

  dependencies.onEvent?.onTokensAdded?.(eventInstance, tempTokens);

  eventInstance.tokensCollected += tempTokens;
  eventInstance.tempTokens = 0;

  fillTokensRequirementsForRewards(dependencies, eventInstance);

  saveToUserData(dependencies, eventInstance, [tempTokens, ...collectionEvent.tokensCollectedHistory].slice(0, 10));
}

export function addTempTokensToCollectionEvent(dependencies: CollectionEventDependencies, amount: number): void {
  const eventInstance = getCurrentEventInstance(dependencies);
  if (!eventInstance) return;
  eventInstance.tempTokens += amount;
  saveToUserData(dependencies, eventInstance);
}

export function clearCollectionEventTempTokens(dependencies: CollectionEventDependencies): void {
  const eventInstance = getCurrentEventInstance(dependencies);
  if (!eventInstance) return;
  eventInstance.tempTokens = 0;
  saveToUserData(dependencies, eventInstance);
}

function saveToUserData(
  dependencies: CollectionEventDependencies,
  eventInstance: CollectionEventConfig,
  tokensHistory?: number[],
): void {
  const { observable } = dependencies;
  const { collectionEvent } = observable.getValue();
  observable.updateValue({
    collectionEvent: {
      ...collectionEvent,
      currentEvent: eventInstance,
      tokensCollectedHistory: tokensHistory ?? collectionEvent.tokensCollectedHistory,
    },
  });
}

function getMedianForLast10Levels(dependencies: CollectionEventDependencies): number {
  const { observable } = dependencies;
  const { collectionEvent } = observable.getValue();
  if (!collectionEvent) {
    return 20;
  }
  const median = MathUtils.getMedian(collectionEvent.tokensCollectedHistory.slice());

  return median > 0 ? median : 100;
}

function roundTokensRequirement(tokenAmount: number): number {
  let roundTo = 10;

  if (tokenAmount > 300) {
    roundTo = 100;
  } else if (tokenAmount > 100) {
    roundTo = 50;
  }

  return Math.round(tokenAmount / roundTo) * roundTo;
}

export function getRewardConfig(
  eventInstance: CollectionEventConfig,
  rewardIndex: number,
): RewardConfig | RewardConfigToCalculate {
  if (rewardIndex < eventInstance.rewards.length) {
    return eventInstance.rewards[rewardIndex];
  }
  const infiniteRewards = eventInstance.rewards.slice(eventInstance.rewardsLoop.start, eventInstance.rewardsLoop.end);
  const infiniteRewardIndex = (rewardIndex - eventInstance.rewards.length) % infiniteRewards.length;
  return infiniteRewards[infiniteRewardIndex];
}

export function fillTokensRequirementsForRewards(
  dependencies: CollectionEventDependencies,
  eventInstance: CollectionEventConfig,
): void {
  let tokensLeft = eventInstance.tokensCollected;

  for (let rewardIndex = 0; rewardIndex < eventInstance.rewards.length; rewardIndex += 1) {
    const rewardConfig = eventInstance.rewards[rewardIndex];

    if (rewardConfig.tokensReq === null) {
      eventInstance.rewards[rewardIndex] = {
        rewards: rewardConfig.rewards,
        tokensReq: roundTokensRequirement(
          getMedianForLast10Levels(dependencies) * rewardConfig.calculation.medianMultiplier,
        ),
      };
    }

    const tokensReq = eventInstance.rewards[rewardIndex].tokensReq ?? 0;

    if (tokensLeft < tokensReq) break;
    tokensLeft -= tokensReq;
  }
}

export function getCollectionEventRewardProgress(
  eventInstance: CollectionEventConfig,
  tokens: number,
): CollectionEventRewardProgress {
  let tokensLeft = tokens;
  let currentRewardIndex = 0;

  for (let i = 0; i < 1000; i += 1) {
    currentRewardIndex = i;

    const rewardConfig = getRewardConfig(eventInstance, i);

    if (rewardConfig.tokensReq === null) {
      console.warn('at this point tokenReq should be calculated');
      currentRewardIndex = Math.max(0, i - 1);
      break;
    }

    const tokensReq = rewardConfig.tokensReq ?? 500;

    if (tokensLeft < tokensReq) break;
    tokensLeft -= tokensReq;
  }

  return {
    progress: tokensLeft,
    target: getRewardConfig(eventInstance, currentRewardIndex).tokensReq ?? 666,
    rewardIndex: currentRewardIndex,
    rewards: getRewardConfig(eventInstance, currentRewardIndex).rewards,
  };
}
